import {Component, HostBinding, input, signal} from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ɵFormGroupRawValue,
  ɵTypedOrUntyped
} from "@angular/forms";
import {NgClass} from "@angular/common";
import {ButtonModule} from "primeng/button";
import {EMPTY, finalize, Observable, of, switchMap} from "rxjs";
import {ActivatedRoute} from "@angular/router";

export type SubmitFunction<T> = (formData: T, route: ActivatedRoute) => Observable<unknown> | Promise<unknown>;

type ExtractFormControls<Type> = Type extends FormGroup<infer FormControls> ? FormControls : never;

// From Angular Form implementation
type RawValueOfFormControls<
  TControl extends {
    [K in keyof TControl]: AbstractControl;
  },

> = ɵTypedOrUntyped<TControl, ɵFormGroupRawValue<TControl>, any>; // eslint-disable-line @typescript-eslint/no-explicit-any

export type RawValueOfFormGroup<T extends FormGroup> = RawValueOfFormControls<
  ExtractFormControls<T>
>;


@Component({
  selector: 'homey-custom-form',
  standalone: true,
  imports: [
    ButtonModule,
    FormsModule,
    ReactiveFormsModule,
    NgClass
  ],
  templateUrl: './custom-form.component.html',
  styleUrl: './custom-form.component.scss'
})
export class CustomFormComponent<T extends FormGroup> {
  @HostBinding('class') readonly class = 'flex-grow flex flex-col';
  public readonly formGroup = input.required<T>();
  public readonly validationFunction = input<(formData: RawValueOfFormGroup<T>) => Observable<boolean>>(
    () => of(true)
  );
  public readonly submitButtonLabel = input.required<string>();
  public readonly submitFunction = input.required<SubmitFunction<RawValueOfFormGroup<T>>>();
  protected readonly state = signal<'default' | 'validating' | 'loading'>('default');
  public readonly noButtonPadding = input(false);
  public readonly buttonDescription = input<string | undefined>(undefined);
  public readonly buttonSize = input<'small' | 'large' | undefined>(undefined);
  public readonly invalidMode = input<'disabled-button' | 'show-errors'>('disabled-button');

  constructor(private readonly route: ActivatedRoute) {
  }

  onSubmitPress() {
    if (this.state() !== 'default') return;

    this.formGroup().markAllAsTouched();

    if (this.formGroup().invalid) {
      if (this.invalidMode() === 'show-errors') {
        this.markAllAsDirty(this.formGroup());
      }
      return;
    }

    this.state.set('validating');
    this.validationFunction()(this.formGroup().getRawValue()).pipe(
      switchMap((result) => {
        if (!result) return EMPTY;
        this.state.set('loading');
        return this.submitFunction()(this.formGroup().getRawValue(), this.route);
      }),
      finalize(() => {
        this.state.set('default')
      })
    ).subscribe()
  }

  private markAllAsDirty(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsDirty();
      if (control instanceof FormGroup) {
        this.markAllAsDirty(control);
      }
    });
  }
}
